import { FadeScroller } from "@components/atoms/FadeScroller"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Button } from "@components/ui/Button"
import { useSiteTheme } from "@features/header/header-theme"
import { ResponsiveImageFragment } from "@generated-types"
import { cn, tv, VariantProps } from "@utils/tw"
import { ReactNode } from "react"

type Props = {
  title?: ReactNode
  description?: ReactNode
  image?: ResponsiveImageFragment | { srcSet?: string | null }
  imageOverlay?: ReactNode
  actions?: ReactNode
  buttons?: ReactNode
  theme?: VariantProps<typeof createStyles>["theme"]
  trackingType?: string
  trackingTitle?: string
  className?: string
  onDismiss?: () => void
}

export function HeroSearchWidget(props: Props) {
  const theme = useSiteTheme()
  const styles = createStyles({
    theme: props.theme ?? (theme.page === "dark" ? "darkMid" : "dark"),
  })

  return (
    <div
      className={cn(styles.base(), props.className)}
      data-mixpanel-widget-type={props.trackingType}
      data-mixpanel-widget-title={props.trackingTitle ?? props.title}
    >
      {props.onDismiss && (
        <Button
          className="absolute top-1 right-1"
          variant="ghost"
          shape="icon"
          icon="cross"
          onClick={props.onDismiss}
        />
      )}
      <div className={styles.header({ dismissable: !!props.onDismiss })}>
        {props.image && (
          <div className={styles.image()}>
            <ResponsiveImage {...props.image!} aspect={4 / 3} />
            {props.imageOverlay}
          </div>
        )}
        <div className="flex flex-col gap-[8px]">
          <h4 className="type-ui-hero">{props.title}</h4>
          <div className="type-body-s text-fg/80 line-clamp-3">
            {typeof props.description === "string" ? (
              <div dangerouslySetInnerHTML={{ __html: props.description! }} />
            ) : (
              props.description
            )}
          </div>
        </div>
      </div>
      {props.actions && (
        <div className={styles.actions()}>
          <FadeScroller fadeSize="lg">
            <div className="flex gap-3 w-min">{props.actions}</div>
          </FadeScroller>
        </div>
      )}
      {props.buttons && <div className={styles.buttons()}>{props.buttons}</div>}
    </div>
  )
}

const createStyles = tv({
  base: "@container rounded-[12px] p-[12px] relative",
  slots: {
    header: "flex flex-row gap-4",
    actions: "flex gap-[8px] justify-stretch [&>*]:flex-1 mt-3",
    buttons: "flex gap-[8px] justify-stretch [&>*]:flex-1 mt-4",
    image: "w-[30cqw] rounded-[8px] overflow-hidden flex-none relative",
  },
  variants: {
    theme: {
      dark: "theme-dark bg-bg text-fg [.small-results_&]:border border-bg-low [[data-search-selected]_&]:bg-neutral-900",
      darkMid:
        "theme-dark bg-bg-low/20 text-fg [.small-results_&]:border border-bg-low [[data-search-selected]_&]:bg-neutral-900",
      light: "theme-light bg-bg text-fg",
      subtle: "bg-bg-low text-fg border border-bg-mid",
      orange: "bg-orange theme-light text-fg",
    },
    dismissable: {
      true: {
        header: "mr-[30px]",
      },
    },
  },
})
